import { apiClient } from './api';
import { AppRoutes } from './appRoutes';

/**
 * START THE BATCH REVIEW REQUEST BEFORE THE SCREEN EXISTS.
 *
 * The batch GET used to be issued only after TWO round-trips it does not depend on, run strictly in
 * series:
 *
 *   1. `AuthProvider` renders `{!loadingInitial && children}` and wraps the whole app, so NO route is
 *      mounted at all until `GET /Auth/IsAuthenthenticated` resolves;
 *   2. `FeatureFlagGate` returns `null` until `GET /config/feature-flags` resolves — and that fetch
 *      cannot even START until (1) has set `user.token`, because it is keyed on it.
 *
 * Measured on a production build of `main` (34-row population, stub at Staging latencies): auth
 * finished at 858 ms, flags at 1053 ms, and the batch GET was not queued until 1096 ms — a screen
 * whose data takes ~2.2 s to build does not ask for it for the first second.
 *
 * ⚠️ NEITHER GATE CHANGES WHAT IS REQUESTED. The bearer is already in localStorage synchronously, and
 * the feature flag decides whether the SCREEN renders, not what it asks the server for. They are
 * accidental dependencies of the request, not real ones. So the request is started here, at module
 * evaluation, in parallel with both, and the screen adopts the in-flight promise when it mounts.
 *
 * ⚠️ ONLY THE UNSCOPED DEFAULT IS PREFETCHED. If the URL promises a narrower scope — `?partnersId`,
 * `?stateId`, `?CustomerPolicyId` — the correct scope is not known until an async lookup settles, and
 * prefetching the whole book would be the expensive wrong query. Those arrivals fall through to the
 * screen's own load, which already waits for the scope to settle.
 */

/**
 * The one prefetch in flight, if any. Consumed once, by the first loader whose key AND bearer match.
 *
 * ⚠️ THE BEARER IS PART OF THE IDENTITY, NOT JUST BOOKKEEPING. This module lives for the lifetime of
 * the tab, and logout/login is a client-side navigation here — no reload — so a prefetch primed for
 * one filer can still be sitting here when a DIFFERENT filer logs in. Handing that response over
 * would show one user a batch fetched under another user's authorization. Matching on the token the
 * screen actually validated makes that impossible.
 */
let inFlight: { key: string; token: string; promise: Promise<any> } | null = null;

/**
 * Mirrors BATCH_FETCH_SIZE in BatchFilingReview. Duplicated deliberately rather than imported: this
 * module is evaluated at startup and must not pull the entire Batch Review screen (and its MUI grid)
 * into the startup path. A test pins the two together so they cannot drift.
 */
export const BATCH_PREFETCH_SIZE = 500;

/**
 * How long an unclaimed prefetch is held. Long enough to cover a slow auth round-trip plus render,
 * short enough that a response nobody wants is not retained for the whole session.
 */
const PREFETCH_TTL_MS = 30_000;

/** URL params that promise a narrower scope than the unscoped default. */
const SCOPE_PARAMS = ['partnersId', 'stateId', 'CustomerPolicyId', 'customerPolicyId'];

/** The bearer the app will use, read synchronously — the same source `AuthProvider` bootstraps from. */
function tokenFromStorage(): string | null {
    try {
        const raw = localStorage.getItem('user');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return typeof parsed?.token === 'string' && parsed.token ? parsed.token : null;
    } catch {
        // A corrupt or unreadable `user` blob is the auth bootstrap's problem, not ours: fall through
        // and let the screen load normally rather than throwing during module evaluation.
        return null;
    }
}

/**
 * Called once at startup, before React renders. Issues the unscoped batch request when — and only
 * when — this navigation is certainly going to need it.
 */
export function primeBatchReviewLoad(
    location?: { pathname: string; search: string },
    fetcher: typeof apiClient = apiClient,
): void {
    if (inFlight) return;
    // ⚠️ THE `window` LOOKUP MUST HAPPEN HERE, IN THE BODY, NOT AS A DEFAULT PARAMETER. Default
    // parameters are evaluated on invocation BEFORE the body runs, so `location = window.location`
    // would throw in any environment without a document — the exact case this guard exists to handle —
    // and the guard below could never run. Module evaluation happens in Node too (tests importing the
    // app, any future SSR/prerender step).
    if (!location && typeof window === 'undefined') return;
    const target = location ?? window.location;
    if (target.pathname !== AppRoutes.filingQueue.batchReview) return;

    const params = new URLSearchParams(target.search);
    if (SCOPE_PARAMS.some((p) => params.has(p))) return;

    const token = tokenFromStorage();
    if (!token) return;

    const query = new URLSearchParams({
        partnersId: '0',
        stateId: '0',
        pageNumber: '1',
        pageSize: String(BATCH_PREFETCH_SIZE),
        includeFields: 'false',
    }).toString();

    const promise = fetcher(`/AutomaticFiling/BatchReview?${query}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    // Nothing awaits this until the screen mounts, and the screen may never mount (auth fails, the
    // flag is off, the filer navigates away). Attaching a handler here marks the rejection handled so
    // it cannot surface as an unhandledrejection; the stored promise still rejects for whoever adopts
    // it, because this handler is attached to a SEPARATE derived promise that is then discarded.
    promise.catch(() => { /* see above */ });

    inFlight = { key: query, token, promise };

    // ⚠️ RELEASE IT IF NOBODY CLAIMS IT. When the screen never mounts, nothing ever calls
    // `takeBatchReviewLoad`, and without this the resolved body -- up to a full batch of rows -- stays
    // reachable from this module for the lifetime of the tab. Expiring the slot lets it be collected.
    // A load slower than this simply falls back to the screen's own request, which is correct, just
    // not early.
    const expiring = inFlight;
    setTimeout(() => { if (inFlight === expiring) inFlight = null; }, PREFETCH_TTL_MS);
}

/**
 * Hand the prefetched response to the screen if it is asking for exactly the same thing, as the same
 * user. Consumed at most once: a later reload (filter change, manual refresh) must issue a real
 * request, not replay a response from page load.
 */
export function takeBatchReviewLoad(key: string, token: string): Promise<any> | null {
    if (!inFlight || inFlight.key !== key || inFlight.token !== token) return null;
    const { promise } = inFlight;
    inFlight = null;
    return promise;
}

/** Test seam — drops any pending prefetch. */
export function resetBatchReviewPrefetch(): void {
    inFlight = null;
}
